/**
 * @license
 *
 * Copyright IBM Corp. 2020, 2024
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { css, html } from 'lit';
import settings from '@carbon/ibmdotcom-utilities/es/utilities/settings/settings.js';
import StableSelectorMixin from '../../globals/mixins/stable-selector';
import C4DContentBlock from '../content-block/content-block';
import styles from './card-section-offset.scss';
import { carbonElement as customElement } from '@carbon/web-components/es/globals/decorators/carbon-element.js';

const { prefix, stablePrefix: c4dPrefix } = settings;

/**
 * Card section offset.
 *
 * @element c4d-card-section-offset
 * @slot heading - The heading content.
 * @slot action - The text-cta content.
 * @slot card-group - The L shaped card group content.
 * @csspart content - The text cta content. Usage: `c4d-card-section-offset::part(content)`
 */
@customElement(`${c4dPrefix}-card-section-offset`)
class C4DCardSectionOffset extends StableSelectorMixin(C4DContentBlock) {
  protected _handleSlotChangeCardGroup(event: Event) {
    const childItems = (event.target as HTMLSlotElement).assignedNodes();

    childItems.filter((elem) => {
      if (
        (elem as HTMLElement).matches(
          (this.constructor as typeof C4DCardSectionOffset).cardGroupSelector
        ) &&
        (elem as HTMLElement).querySelectorAll(`${c4dPrefix}-card-group-item`)
          .length === 3
      ) {
        const emptyCard = document.createElement(
          `${c4dPrefix}-card-group-item`
        );
        emptyCard.setAttribute('empty', '');
        this.querySelector(`${c4dPrefix}-card-group`)?.prepend(emptyCard);
        return true;
      }
      this.querySelector(`${c4dPrefix}-card-group`)!.innerHTML = '';
      return false;
    });
  }

  render() {
    return html`
      <slot name="image-top"></slot>
      <div class="${prefix}--card-section-offset__content" part="content">
        ${this._renderHeading()}
        <slot name="action"></slot>
      </div>
      <slot
        name="card-group"
        @slotchange="${this._handleSlotChangeCardGroup}"></slot>
      <slot name="image-bottom"></slot>
    `;
  }

  static get stableSelector() {
    return `${c4dPrefix}--card-section-offset`;
  }

  static get cardGroupSelector() {
    return `${c4dPrefix}-card-group`;
  }

  // `styles` here is a `CSSResult` generated by custom WebPack loader
  static get styles() {
    return css`
      ${super.styles}${styles}
    `;
  }
}

/* @__GENERATE_REACT_CUSTOM_ELEMENT_TYPE__ */
export default C4DCardSectionOffset;
